import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { UserPlus } from "lucide-react";
import Logo from "@/components/Logo";
import api, { fmtErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function InvitePage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invites/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(fmtErr(e, t.inv.invalid)));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/invites/${token}/accept`);
      navigate("/app");
    } catch (e) {
      setError(fmtErr(e));
      setBusy(false);
    }
  };

  return (
    <div className="grid-bg flex min-h-screen items-center justify-center bg-[#FDFDFD] px-5 py-16">
      <div className="w-full max-w-md">
        <div className="flex justify-center"><Link to="/" data-testid="invite-logo-link"><Logo /></Link></div>
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-slate-900">
            <UserPlus className="h-5 w-5 text-white" />
          </div>
          <h1 className="mt-5 font-display text-2xl font-medium tracking-tight text-slate-900">{t.inv.title}</h1>
          {error && !info && (
            <p className="mt-4 rounded-md border border-red-100 bg-red-50 px-3.5 py-2.5 text-sm text-red-600" data-testid="invite-error">{error}</p>
          )}
          {info && (
            <div className="mt-4 space-y-4">
              <p className="text-sm leading-relaxed text-slate-600" data-testid="invite-info">
                <span className="font-medium text-slate-900">{info.inviter || info.company}</span> {t.inv.invitedBy}
              </p>
              <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-left text-xs text-slate-500">
                <p><span className="text-slate-400">{t.inv.emailNote}:</span> <span className="font-mono text-slate-700">{info.email}</span></p>
                <p className="mt-1"><span className="text-slate-400">{t.inv.asRole}:</span> <span className="font-medium text-slate-700">{t.team[info.role] || info.role}</span></p>
              </div>
              {user ? (
                <Button onClick={accept} disabled={busy} data-testid="invite-accept-button" className="w-full">
                  {busy ? t.auth.working : t.inv.join}
                </Button>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-slate-500">{t.inv.needAuth}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Button variant="outline" asChild data-testid="invite-to-login">
                      <Link to={`/giris?davet=${token}`}>{t.auth.login}</Link>
                    </Button>
                    <Button asChild data-testid="invite-to-register">
                      <Link to={`/kayit?davet=${token}`}>{t.auth.register}</Link>
                    </Button>
                  </div>
                </div>
              )}
              {error && (
                <p className="rounded-md border border-red-100 bg-red-50 px-3.5 py-2.5 text-sm text-red-600" data-testid="invite-accept-error">{error}</p>
              )}
            </div>
          )}
          {!info && !error && <p className="mt-4 text-sm text-slate-400">{t.app.loading}</p>}
        </div>
      </div>
    </div>
  );
}
